/* eslint-disable @typescript-eslint/no-redeclare */
import { z } from 'zod';

/**
 * These types need to be synced with the API types.
 *
 * See the types in simple-store-locator/src/pages/api/locator.ts
 */
export const TranslationTarget = z.enum([
  'searchPlaceholder',
  'noLocationsFound',
  'getDirections',
  'email',
  'phone',
  'website',
  'notes',
]);
export type TranslationTarget = z.infer<typeof TranslationTarget>;

export const DistanceUnit = z.enum(['km', 'mi']);
export type DistanceUnit = z.infer<typeof DistanceUnit>;

export const FieldVisibilityTarget = z.enum([
  'phone',
  'email',
  'website',
  'notes',
  'searchFilters',
]);
export type FieldVisibilityTarget = z.infer<typeof FieldVisibilityTarget>;

export const FieldVisibilityScope = z.enum(['list', 'map']);
export type FieldVisibilityScope = z.infer<typeof FieldVisibilityScope>;

export const CustomFieldType = z.enum(['plain', 'multiline']);
export type CustomFieldType = z.infer<typeof CustomFieldType>;

export const LocatorResponse = z.object({
  stores: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      active: z.boolean(),
      phone: z.string(),
      email: z.string(),
      website: z.string(),
      address1: z.string(),
      address2: z.string(),
      city: z.string(),
      province: z.string(),
      zip: z.string(),
      country: z.string(),
      notes: z.string(),
      lat: z.number(),
      lng: z.number(),
      searchFilters: z.array(z.object({ id: z.string(), name: z.string() })),
      customFieldValues: z.array(
        z.object({
          fieldId: z.string(),
          value: z.string(),
        }),
      ),
    }),
  ),
  customFields: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      position: z.number(),
      type: CustomFieldType,
    }),
  ),
  settings: z.object({
    markerColor: z.string(),
    themeColor: z.string(),
    distanceUnit: DistanceUnit,
    showDetailsLabels: z.boolean(),
    limitStorefrontList: z.number(),
    defaultMapZoom: z.number(),
  }),
  translations: z.array(
    z.object({
      id: z.string(),
      language: z.string(),
      target: TranslationTarget.nullable(),
      customFieldId: z.string().nullable(),
      searchFilterId: z.string().nullable(),
      value: z.string(),
    }),
  ),
  fieldVisibilities: z.array(
    z.object({
      id: z.string(),
      target: FieldVisibilityTarget.nullable(),
      customFieldId: z.string().nullable(),
      visible: z.boolean(),
      scope: FieldVisibilityScope,
    }),
  ),
});
export type LocatorResponse = z.infer<typeof LocatorResponse>;

/**
 * This object was created from the API Response.
 *
 * @see SuccessResponse interface in simple-store-locator/src/pages/api/ipstack.ts
 */
export const IpstackResponse = z.object({
  ipstack: z.object({
    latitude: z.number(),
    longitude: z.number(),
  }),
});
export type IpstackResponse = z.infer<typeof IpstackResponse>;
